import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Mail, 
  Phone, 
  MapPin, 
  Trash2, 
  Edit2, 
  Loader2,
  X,
  User,
  Building2,
  Globe,
  CreditCard,
  History,
  Briefcase,
  Calendar,
  DollarSign
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDoc } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';
import ConfirmModal from './ConfirmModal';
import EmployeeImport from './EmployeeImport';
import { FileUp } from 'lucide-react';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  department: string;
  status: 'Active' | 'Inactive' | 'On Leave';
  joiningDate: string;
  salary: number;
  paymentFrequency: 'Monthly' | 'Weekly' | 'Bi-weekly';
  bankName?: string;
  accountNumber?: string;
  notes?: string;
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

type ApiEmployee = Record<string, unknown>;

const normalizeEmployee = (employee: ApiEmployee): Employee => {
  const status = employee.status === 'Inactive' || employee.status === 'On Leave'
    ? employee.status
    : 'Active';
  const paymentFrequency = employee.paymentFrequency === 'Weekly' || employee.payment_frequency === 'Weekly'
    ? 'Weekly'
    : employee.paymentFrequency === 'Bi-weekly' || employee.payment_frequency === 'Bi-weekly'
      ? 'Bi-weekly'
      : 'Monthly';

  return {
    id: String(employee.id ?? ''),
    firstName: String(employee.firstName ?? employee.first_name ?? ''),
    lastName: String(employee.lastName ?? employee.last_name ?? ''),
    email: String(employee.email ?? ''),
    phone: String(employee.phone ?? ''),
    jobTitle: String(employee.jobTitle ?? employee.job_title ?? ''),
    department: String(employee.department ?? ''),
    status,
    joiningDate: String(employee.joiningDate ?? employee.joining_date ?? ''),
    salary: Number(employee.salary ?? 0),
    paymentFrequency,
    bankName: String(employee.bankName ?? employee.bank_name ?? ''),
    accountNumber: String(employee.accountNumber ?? employee.account_number ?? ''),
    notes: String(employee.notes ?? ''),
    createdAt: employee.createdAt ?? employee.created_at,
    updatedAt: employee.updatedAt ?? employee.updated_at,
    createdBy: typeof employee.createdBy === 'string'
      ? employee.createdBy
      : typeof employee.created_by === 'string'
        ? employee.created_by
        : undefined,
  };
};

import EmployeeProfileView from './EmployeeProfileView';

export default function EmployeesView({ orgId, orgSettings, language = 'en' }: { orgId: string, orgSettings?: any, language?: string }) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [currency, setCurrency] = useState(getDefaultCurrency(orgSettings));

  useEffect(() => {
    if (orgSettings?.defaultCurrency) {
      setCurrency(getDefaultCurrency(orgSettings));
    }
  }, [orgSettings?.defaultCurrency]);

  const [newEmployee, setNewEmployee] = useState<Omit<Employee, 'id'>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    status: 'Active',
    joiningDate: new Date().toISOString().split('T')[0],
    salary: 0,
    paymentFrequency: 'Monthly',
    bankName: '',
    accountNumber: '',
    notes: ''
  });

  useEffect(() => {
    if (!orgId) return;

    const localAuthToken = typeof window !== 'undefined'
      ? localStorage.getItem('app_auth_token')
      : null;

    if (localAuthToken) {
      let cancelled = false;

      const fetchLocalEmployees = async () => {
        try {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/employees`, {
            headers: { Authorization: `Bearer ${localAuthToken}` },
          });
          if (!response.ok) throw new Error(`Failed to load employees: ${response.status}`);

          const data = await response.json();
          if (!cancelled) {
            setEmployees((Array.isArray(data) ? data : []).map(normalizeEmployee));
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error fetching employees:", error);
          if (!cancelled) {
            notify.error("Failed to load employees");
            setIsLoading(false);
          }
        }
      };

      fetchLocalEmployees();
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'employees'),
      where('orgId', '==', orgId),
      orderBy('firstName', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const employeeData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Employee[];
      setEmployees(employeeData);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching employees:", error);
      notify.error("Failed to load employees");
      setIsLoading(false);
      handleFirestoreError(error, OperationType.LIST, 'employees');
    });

    // Fetch currency from payroll settings
    const fetchCurrency = async () => {
      try {
        const docRef = doc(db, 'organizations', orgId, 'settings', 'payroll');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.currency) setCurrency(data.currency);
        }
      } catch (error) {
        console.error("Error fetching currency:", error);
      }
    };
    fetchCurrency();

    return () => unsubscribe();
  }, [orgId]);

  const handleSaveEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const employeeData = {
        ...newEmployee,
        orgId,
        updatedAt: serverTimestamp()
      };

      if (isEditing && selectedEmployee) {
        const localAuthToken = typeof window !== 'undefined'
          ? localStorage.getItem('app_auth_token')
          : null;

        if (localAuthToken) {
          const response = await fetch(
            `/api/organizations/${encodeURIComponent(orgId)}/employees/${encodeURIComponent(selectedEmployee.id)}`,
            {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${localAuthToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(newEmployee),
            }
          );
          if (!response.ok) throw new Error(`Failed to update employee: ${response.status}`);

          const updatedEmployee = normalizeEmployee(await response.json());
          setEmployees(currentEmployees =>
            currentEmployees.map(employee =>
              employee.id === selectedEmployee.id ? updatedEmployee : employee
            ).sort((a, b) => a.firstName.localeCompare(b.firstName))
          );
        } else {
          await updateDoc(doc(db, 'employees', selectedEmployee.id), employeeData);
        }
        notify.confirm("Employee updated successfully");
      } else {
        const localAuthToken = typeof window !== 'undefined'
          ? localStorage.getItem('app_auth_token')
          : null;

        if (localAuthToken) {
          const response = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/employees`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localAuthToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(newEmployee),
          });
          if (!response.ok) throw new Error(`Failed to create employee: ${response.status}`);

          const createdEmployee = normalizeEmployee(await response.json());
          setEmployees(currentEmployees =>
            [...currentEmployees, createdEmployee].sort((a, b) => a.firstName.localeCompare(b.firstName))
          );
        } else {
          await addDoc(collection(db, 'employees'), {
            ...employeeData,
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp()
          });
        }
        notify.confirm("Employee added successfully");
      }
      setIsModalOpen(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving employee:", error);
      notify.error("Failed to save employee");
      handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'employees');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      const localAuthToken = typeof window !== 'undefined'
        ? localStorage.getItem('app_auth_token')
        : null;

      if (localAuthToken) {
        const response = await fetch(
          `/api/organizations/${encodeURIComponent(orgId)}/employees/${encodeURIComponent(id)}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${localAuthToken}` },
          }
        );
        if (!response.ok) throw new Error(`Failed to delete employee: ${response.status}`);

        setEmployees(currentEmployees => currentEmployees.filter(employee => employee.id !== id));
      } else {
        await deleteDoc(doc(db, 'employees', id));
      }
      notify.confirm("Employee deleted successfully");
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting employee:", error);
      notify.error("Failed to delete employee");
      handleFirestoreError(error, OperationType.DELETE, 'employees');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                         emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.jobTitle.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || emp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (viewingEmployee) {
    return (
      <EmployeeProfileView 
        employee={viewingEmployee} 
        orgId={orgId} 
        currency={currency}
        onBack={() => setViewingEmployee(null)}
        onEdit={() => {
          setSelectedEmployee(viewingEmployee);
          setNewEmployee({ ...viewingEmployee } as any);
          setIsEditing(true);
          setIsModalOpen(true);
        }}
      />
    );
  }

  return (
    <div className="space-y-6 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Employees</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Manage your workforce and payroll details</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center gap-2 border border-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold"
          >
            <FileUp className="w-4 h-4" />
            <span>Import Employees</span>
          </button>
          <button 
            onClick={() => {
              setIsEditing(false);
              setNewEmployee({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                jobTitle: '',
                department: '',
                status: 'Active',
                joiningDate: new Date().toISOString().split('T')[0],
                salary: 0,
                paymentFrequency: 'Monthly',
                bankName: '',
                accountNumber: '',
                notes: ''
              });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
          >
            <Plus className="w-4 h-4" />
            <span>Add Employee</span>
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {['All', 'Active', 'Inactive', 'On Leave'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border shrink-0",
                statusFilter === status 
                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                  : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
              )}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input 
            type="text" 
            placeholder="Search employees..." 
            className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading employees...</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <User className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No employees found</p>
          <p className="text-sm opacity-30 mt-2">Add your first employee to start managing payroll</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmployees.map((emp) => (
            <div 
              key={emp.id}
              className="bg-white/50 border border-[#141414] rounded-3xl p-6 hover:shadow-xl transition-all group relative overflow-hidden cursor-pointer"
              onClick={() => {
                setViewingEmployee(emp);
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#141414] text-[#E4E3E0] rounded-2xl flex items-center justify-center font-bold text-xl">
                  {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-[10px] font-bold border",
                    emp.status === 'Active' ? "bg-green-100 text-green-700 border-green-200" : 
                    emp.status === 'On Leave' ? "bg-orange-100 text-orange-700 border-orange-200" :
                    "bg-gray-100 text-gray-700 border-gray-200"
                  )}>
                    {emp.status}
                  </span>
                </div>
              </div>

              <div className="space-y-1 mb-6">
                <h3 className="text-lg font-bold tracking-tight truncate group-hover:text-orange-600 transition-colors">{emp.firstName} {emp.lastName}</h3>
                <p className="text-xs opacity-50 font-mono">{emp.jobTitle} • {emp.department}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{emp.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <Phone className="w-4 h-4" />
                  <span>{emp.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-sm opacity-70">
                  <div className="w-4 h-4 flex items-center justify-center text-[10px] font-bold border border-current rounded-sm">{currency}</div>
                  <span>{(emp.salary || 0).toLocaleString()} / {emp.paymentFrequency || 'month'}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-[#141414]/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 opacity-30" />
                  <p className="text-[10px] uppercase font-bold opacity-40">Joined {emp.joiningDate ? new Date(emp.joiningDate).toLocaleDateString() : 'N/A'}</p>
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteId(emp.id);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] w-full max-w-4xl rounded-[32px] overflow-hidden shadow-2xl border border-[#141414] flex flex-col max-h-[calc(100dvh-2rem)]">
            <div className="p-8 border-b border-[#141414] flex items-center justify-between bg-white/50">
              <div>
                <h2 className="text-2xl font-bold italic serif tracking-tight">
                  {isEditing ? 'Edit Employee' : 'Add New Employee'}
                </h2>
                <p className="text-sm opacity-50 italic serif">Enter employee details and payroll information</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSaveEmployee} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Personal Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">First Name *</label>
                      <input 
                        required
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        placeholder="John"
                        value={newEmployee.firstName}
                        onChange={(e) => setNewEmployee({...newEmployee, firstName: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Last Name *</label>
                      <input 
                        required
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        placeholder="Doe"
                        value={newEmployee.lastName}
                        onChange={(e) => setNewEmployee({...newEmployee, lastName: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Email Address *</label>
                    <input 
                      required
                      type="email" 
                      className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                      placeholder="john.doe@company.com"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Phone Number</label>
                    <input 
                      type="tel" 
                      className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                      placeholder="+1 234 567 890"
                      value={newEmployee.phone}
                      onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                    />
                  </div>
                </div>

                {/* Employment Information */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Employment Details</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Job Title *</label>
                      <input 
                        required
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        placeholder="Software Engineer"
                        value={newEmployee.jobTitle}
                        onChange={(e) => setNewEmployee({...newEmployee, jobTitle: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Department</label>
                      <input 
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                        placeholder="Engineering"
                        value={newEmployee.department}
                        onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Status</label>
                      <select 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
                        value={newEmployee.status}
                        onChange={(e) => setNewEmployee({...newEmployee, status: e.target.value as any})}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="On Leave">On Leave</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Joining Date</label>
                      <input 
                        type="date" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                        value={newEmployee.joiningDate}
                        onChange={(e) => setNewEmployee({...newEmployee, joiningDate: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Payroll Information */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Payroll & Banking</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Base Salary *</label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] font-bold opacity-30">{currency}</div>
                        <input 
                          required
                          type="number" 
                          className="w-full bg-white/50 border border-[#141414] pl-12 pr-4 py-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                          placeholder="0.00"
                          value={isNaN(newEmployee.salary) ? '' : (newEmployee.salary === 0 ? '' : newEmployee.salary)}
                          onChange={(e) => setNewEmployee({...newEmployee, salary: e.target.value === '' ? 0 : parseFloat(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Payment Frequency</label>
                      <select 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
                        value={newEmployee.paymentFrequency}
                        onChange={(e) => setNewEmployee({...newEmployee, paymentFrequency: e.target.value as any})}
                      >
                        <option value="Monthly">Monthly</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Bi-weekly">Bi-weekly</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Bank Name</label>
                      <input 
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                        placeholder="Global Bank"
                        value={newEmployee.bankName}
                        onChange={(e) => setNewEmployee({...newEmployee, bankName: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest font-bold opacity-50">Account Number</label>
                      <input 
                        type="text" 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all"
                        placeholder="1234567890"
                        value={newEmployee.accountNumber}
                        onChange={(e) => setNewEmployee({...newEmployee, accountNumber: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 border-b border-[#141414]/10 pb-2">Additional Notes</h3>
                  <textarea 
                    rows={6}
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all resize-none"
                    placeholder="Internal notes about this employee..."
                    value={newEmployee.notes}
                    onChange={(e) => setNewEmployee({...newEmployee, notes: e.target.value})}
                  />
                </div>
              </div>

              <div className="pt-8 border-t border-[#141414] flex items-center justify-end gap-4">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isEditing ? 'Update Employee' : 'Create Employee'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Employees Modal */}
      {isImportModalOpen && (
        <EmployeeImport 
          orgId={orgId}
          onClose={() => setIsImportModalOpen(false)}
          onComplete={() => {
            setIsImportModalOpen(false);
          }}
        />
      )}

      <ConfirmModal 
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDeleteEmployee(deleteId)}
        title="Delete Employee"
        message="Are you sure you want to delete this employee? This action cannot be undone."
      />
    </div>
  );
}
